import React, { useState } from 'react';
import { Link, Copy, Send, CheckCircle } from 'lucide-react';
import { paymentAPI, CustomerInfo, formatCurrency } from '../../services/paymentAPI';

interface PaymentLinkGeneratorProps {
  invoiceId: number;
  amount: number;
  currency?: string;
  customer: CustomerInfo;
  onLinkGenerated?: (link: string) => void;
}

const PaymentLinkGenerator: React.FC<PaymentLinkGeneratorProps> = ({
  invoiceId,
  amount,
  currency = 'KWD',
  customer,
  onLinkGenerated,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string>('');
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState<string>('');

  const generatePaymentLink = async () => {
    setIsGenerating(true);
    setError('');

    try {
      const response = await paymentAPI.createPaymentLink({
        invoice_id: invoiceId,
        amount,
        currency,
        customer,
        description: `Payment for Invoice #${invoiceId}`,
      });

      if (response.data.payment_url) {
        setPaymentLink(response.data.payment_url);
        if (onLinkGenerated) {
          onLinkGenerated(response.data.payment_url);
        }
      } else {
        setError('Failed to generate payment link');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate payment link');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(paymentLink);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = paymentLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const shareViaWhatsApp = () => {
    const message = `Hi ${customer.name}, please use this link to pay your invoice: ${paymentLink}`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${customer.phone.replace(/[^0-9]/g, '')}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareViaSMS = () => {
    const message = `Hi ${customer.name}, please use this link to pay your invoice: ${paymentLink}`;
    const smsUrl = `sms:${customer.phone}?body=${encodeURIComponent(message)}`;
    window.location.href = smsUrl;
  };

  const shareViaEmail = () => {
    const subject = `Payment Link for Invoice #${invoiceId}`;
    const body = `Hi ${customer.name},\n\nPlease use the following link to pay your invoice:\n\n${paymentLink}\n\nAmount: ${formatCurrency(amount, currency)}\n\nThank you!`;
    const mailtoUrl = `mailto:${customer.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center mb-4">
        <Link className="h-5 w-5 text-blue-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">Payment Link Generator</h3>
      </div>

      <div className="space-y-4">
        {/* Invoice Details */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Invoice ID:</span>
              <span className="ml-2 font-medium">#{invoiceId}</span>
            </div>
            <div>
              <span className="text-gray-600">Amount:</span>
              <span className="ml-2 font-medium">{formatCurrency(amount, currency)}</span>
            </div>
            <div>
              <span className="text-gray-600">Customer:</span>
              <span className="ml-2 font-medium">{customer.name}</span>
            </div>
            <div>
              <span className="text-gray-600">Phone:</span>
              <span className="ml-2 font-medium">{customer.phone}</span>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        {!paymentLink && (
          <button
            onClick={generatePaymentLink}
            disabled={isGenerating}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Generating Link...
              </>
            ) : (
              <>
                <Link className="h-4 w-4 mr-2" />
                Generate Payment Link
              </>
            )}
          </button>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700 text-sm">{typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}</p>
          </div>
        )}

        {/* Payment Link Display */}
        {paymentLink && (
          <div className="space-y-4">
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center mb-2">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-green-700 font-medium">Payment link generated successfully!</span>
              </div>
              <div className="bg-white p-2 rounded border text-sm font-mono break-all">
                {paymentLink}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={copyToClipboard}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                <Copy className="h-4 w-4 mr-2" />
                {isCopied ? 'Copied!' : 'Copy Link'}
              </button>

              <button
                onClick={shareViaWhatsApp}
                className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                <Send className="h-4 w-4 mr-2" />
                WhatsApp
              </button>
            </div>

            {/* Additional Share Options */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={shareViaSMS}
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                <Send className="h-4 w-4 mr-2" />
                SMS
              </button>

              {customer.email && (
                <button
                  onClick={shareViaEmail}
                  className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Email
                </button>
              )}
            </div>

            {/* Reset Button */}
            <button
              onClick={() => {
                setPaymentLink('');
                setError('');
                setIsCopied(false);
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Generate New Link
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentLinkGenerator;